const FRAME_TAGS = [
  { open: '<EDIT', close: '</EDIT>' },
  { open: '<REMOVE', close: '</REMOVE>' }
]

/**
 * Sector control blocks: `<SECTOR_OPTIONS sectors="…" />` offers the SE a choice
 * of cohorts, `<REGENERATE sector="…" />` commits to one. Each is handled as its
 * own stream frame, so neither should reach the chat as text.
 *
 * They need a rule of their own because the scanner below pairs an opening tag
 * with a closing one, and these are self-closing: with no `</REGENERATE>` to
 * find, the scanner walks straight past and the raw markup renders in the chat.
 *
 * The trailing `>?` also matches a half-written tag mid-stream, so it does not
 * flicker into view token by token as the model types it.
 */
const CONTROL_TAG = /<(REGENERATE|SECTOR_OPTIONS|SCOPE_REDIRECT)\b[^>]*>?/gi

function partialOpeningIndex(value: string): number {
  const lower = value.toLowerCase()
  const longest = Math.max(...FRAME_TAGS.map(tag => tag.open.length))
  const start = Math.max(0, value.length - longest + 1)
  for (let index = start; index < value.length; index += 1) {
    const tail = lower.slice(index)
    if (FRAME_TAGS.some(tag => tag.open.toLowerCase().startsWith(tail))) return index
  }
  return -1
}

function nextFrameOpening(value: string): { offset: number; tag: (typeof FRAME_TAGS)[number] } | null {
  const lower = value.toLowerCase()
  let best: { offset: number; tag: (typeof FRAME_TAGS)[number] } | null = null
  for (const tag of FRAME_TAGS) {
    const offset = lower.indexOf(tag.open.toLowerCase())
    if (offset !== -1 && (!best || offset < best.offset)) best = { offset, tag }
  }
  return best
}

export function conversationalAraResponse(value: string): string {
  let cursor = 0
  let response = ''

  while (cursor < value.length) {
    const remaining = value.slice(cursor)
    const opening = nextFrameOpening(remaining)

    if (!opening) {
      const partialOffset = partialOpeningIndex(remaining)
      response += partialOffset === -1 ? remaining : remaining.slice(0, partialOffset)
      break
    }

    response += remaining.slice(0, opening.offset)
    const openIndex = cursor + opening.offset
    const openingEnd = value.indexOf('>', openIndex + opening.tag.open.length)
    if (openingEnd === -1) break

    const closeIndex = value.toLowerCase().indexOf(opening.tag.close.toLowerCase(), openingEnd + 1)
    if (closeIndex === -1) break

    cursor = closeIndex + opening.tag.close.length
  }

  return response
    .replace(CONTROL_TAG, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}
